import React, {useContext, type ComponentProps, type ReactNode} from 'react';
import {Audio as MediaAudio, Video as MediaVideo} from '@remotion/media';
import {Internals, useRemotionEnvironment, useVideoConfig} from 'remotion';
import {PitchAudioContext} from '../render/pitch-audio-context';
import {resolvePreparedPitchAudio} from '../render/pitch-audio-plan';

type AudioProps = ComponentProps<typeof MediaAudio>;
type VideoProps = ComponentProps<typeof MediaVideo>;
type MediaTiming = {playbackRate?: number; durationInFrames?: number; from?: number; loop?: boolean};

/**
 * Remotion 4.0.471 caps an inner media Sequence to parentDuration * playbackRate.
 * For slow media that is shorter than the authored timeline duration. Only the
 * media subtree sees the compensated duration; its physical parent Sequence
 * remains the authority for visibility, frame position, and premounting.
 */
export function BrowserSlowMediaBoundary({timing, children}: {timing: MediaTiming; children: (durationInFrames?: number) => ReactNode}) {
  const parent = useContext(Internals.SequenceContext);
  const {isClientSideRendering} = useRemotionEnvironment();
  const rate = timing.playbackRate ?? 1;
  const from = timing.from ?? 0;
  const authoredDuration = parent && Number.isFinite(parent.durationInFrames) && Number.isFinite(from)
    ? Math.min(timing.durationInFrames ?? Infinity, Math.max(0, parent.durationInFrames - from))
    : null;
  if (!isClientSideRendering || !parent || timing.loop || !Number.isFinite(rate) || rate <= 0 || rate >= 1 ||
      authoredDuration === null || !Number.isFinite(authoredDuration) || authoredDuration <= 0) {
    return <>{children(timing.durationInFrames)}</>;
  }
  const compensatedDuration = Math.ceil(parent.durationInFrames / rate);
  if (!Number.isSafeInteger(compensatedDuration)) return <>{children(timing.durationInFrames)}</>;
  return <Internals.SequenceContext.Provider value={{...parent, durationInFrames: compensatedDuration}}>
    {children(authoredDuration)}
  </Internals.SequenceContext.Provider>;
}

export function BrowserRenderMediaAudio(props: AudioProps) {
  const prepared=usePitchMedia(props);
  return <BrowserSlowMediaBoundary timing={props}>
    {(durationInFrames) => prepared
      ? prepared.entry.url&&<MediaAudio {...props} src={prepared.entry.url} playbackRate={1}
          trimBefore={prepared.trimBefore} trimAfter={prepared.trimAfter} durationInFrames={durationInFrames}
          disallowFallbackToHtml5Audio/>
      : <MediaAudio {...props} durationInFrames={durationInFrames}/>}
  </BrowserSlowMediaBoundary>;
}

export function BrowserRenderMediaVideo(props: VideoProps) {
  const prepared=usePitchMedia(props);
  return <BrowserSlowMediaBoundary timing={props}>
    {(durationInFrames) => <>
      <MediaVideo {...props} muted={prepared?true:props.muted} durationInFrames={durationInFrames}/>
      {prepared?.entry.url&&<MediaAudio src={prepared.entry.url} playbackRate={1}
        trimBefore={prepared.trimBefore} trimAfter={prepared.trimAfter} volume={props.volume}
        from={props.from} durationInFrames={durationInFrames} muted={props.muted}
        toneFrequency={props.toneFrequency} loopVolumeCurveBehavior={props.loopVolumeCurveBehavior}
        delayRenderRetries={props.delayRenderRetries??undefined}
        delayRenderTimeoutInMilliseconds={props.delayRenderTimeoutInMilliseconds??undefined}
        disallowFallbackToHtml5Audio/>}
    </>}
  </BrowserSlowMediaBoundary>;
}

/** Resolve once-prepared source windows against the original local media clock. */
function usePitchMedia(props:AudioProps|VideoProps){
  const entries=useContext(PitchAudioContext);
  const {isClientSideRendering}=useRemotionEnvironment();
  const {fps,durationInFrames}=useVideoConfig();
  const rate=props.playbackRate??1;
  if(!isClientSideRendering||props.muted||rate>=1)return null;
  if(props.loop||(props.audioStreamIndex!==undefined&&props.audioStreamIndex!==0))
    throw Error('반복 재생 또는 다른 음성 스트림의 감속 출력은 구간을 나누거나 원본을 바꾼 뒤 다시 시도해 주세요.');
  const duration=Math.min(props.durationInFrames??Infinity,Math.max(0,durationInFrames-(props.from??0)));
  const start=(props.trimBefore??0)/fps;
  const end=Math.min(props.trimAfter===undefined?Infinity:props.trimAfter/fps,start+duration/fps*rate);
  const entry=resolvePreparedPitchAudio(entries,props.src,rate,start,end);
  return {entry,trimBefore:Math.max(0,(start-entry.start)/rate*fps),
    trimAfter:props.trimAfter===undefined?undefined:Math.max(0,(end-entry.start)/rate*fps)};
}
